import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useShopifyProducts } from "@/hooks/useShopifyProducts";
import ShopifyProductCard from "@/components/ShopifyProductCard";

const FeaturedProducts = () => {
  const FEATURED_EXCLUDE = [
    "the-neo-blush-pro-essentials-set",
    "judydoll-2-in-1-blush-highlight-palette",
    "the-perfection-collection-customizable-talc-free-silicone-free-makeup-set-select-your-own-shades",
  ];
  const { data: allProducts, isLoading, error, refetch } = useShopifyProducts(20);
  const products = (allProducts ?? [])
    .filter((p) => !FEATURED_EXCLUDE.includes(p.node.handle))
    .slice(0, 4);
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['shopify-products'] });
    refetch();
  };
  return (
    <section id="products" className="py-24 bg-background relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-accent/5 rounded-full blur-[80px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-4">
            Featured <span className="text-primary glow-text">Products</span>
          </h2>
          <p className="font-subtitle text-muted-foreground max-w-2xl mx-auto">
            Explore our curated selection of premium beauty essentials, crafted for those who appreciate luxury.
          </p>
        </motion.div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="font-subtitle text-muted-foreground">Unable to load products. Please try again later.</p>
          </div>
        ) : products && products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <ShopifyProductCard key={product.node.id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 space-y-4">
            <p className="font-subtitle text-muted-foreground">No products are currently available. Check back soon!</p>
            <Button variant="glowOutline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh products
            </Button>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button variant="glowOutline" size="lg" asChild>
            <Link to="/shop">View All Products</Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
